import React from "react";
import { Calendar, ExternalLink } from "react-feather";
import {
  KG_INSPECTOR_ICON_DEFAULT_SIZE,
  kgInspectorIcons,
} from "../../kg/canvas/inspector/kgInspectorIcons";
import type { KgInspectorTableSourceRef } from "../../kg/canvas/inspector/types";
import { KgInspectorDetailRow } from "../KgInspectorDetailRow";
import {
  DescriptionBody,
  EmptyHint,
  IconSlot,
  OverviewRoot,
  SectionBlock,
  SectionHeader,
  SectionTitle,
  SourceLinkButton,
  SourceLogo,
  SourceName,
  SourceValue,
} from "./KgInspectorReportOverview.styles";
import type { KgInspectorReportOverviewProps } from "./KgInspectorReportOverview.types";

const DescriptionIcon = kgInspectorIcons.description;
const SourceIcon = kgInspectorIcons.source;
const ReportTypeIcon = kgInspectorIcons.reportPanel;
const sourceIconSize = KG_INSPECTOR_ICON_DEFAULT_SIZE.source;
const reportTypeIconSize = KG_INSPECTOR_ICON_DEFAULT_SIZE.reportPanel;

const SourceRefValue: React.FC<{ source: KgInspectorTableSourceRef }> = ({
  source,
}) => (
  <SourceValue>
    {source.logoUrl && <SourceLogo src={source.logoUrl} alt="" aria-hidden />}
    <SourceName>{source.name}</SourceName>
    {source.externalUrl && (
      <SourceLinkButton
        href={source.externalUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open ${source.name} in a new tab`}
        onClick={(event) => event.stopPropagation()}
      >
        <ExternalLink size={14} />
      </SourceLinkButton>
    )}
  </SourceValue>
);

export const KgInspectorReportOverview: React.FC<
  KgInspectorReportOverviewProps
> = ({ overview }) => {
  const { description, source, reportType, fetchedOn } = overview;

  return (
    <OverviewRoot>
      <SectionBlock>
        <SectionHeader>
          <IconSlot>
            <DescriptionIcon size={16} />
          </IconSlot>
          <SectionTitle>Description</SectionTitle>
        </SectionHeader>
        {description ? (
          <DescriptionBody>{description}</DescriptionBody>
        ) : (
          <EmptyHint>No description available for this report yet.</EmptyHint>
        )}
      </SectionBlock>

      {source && (
        <KgInspectorDetailRow
          icon={<SourceIcon size={sourceIconSize} />}
          label="Source"
          value={<SourceRefValue source={source} />}
        />
      )}

      {reportType && (
        <KgInspectorDetailRow
          icon={<ReportTypeIcon size={reportTypeIconSize} />}
          label="Type"
          value={reportType}
        />
      )}

      {fetchedOn && (
        <KgInspectorDetailRow
          icon={<Calendar size={16} />}
          label="Fetched on"
          value={fetchedOn}
        />
      )}
    </OverviewRoot>
  );
};

export default KgInspectorReportOverview;
